'use client'

import Image from 'next/image'

interface Logo {
  src: string
  alt: string
  width: number
  height: number
  className?: string
}

interface ScrollingLogosProps {
  logos: Logo[]
  backgroundColor?: string
  speed?: 'slow' | 'medium' | 'fast'
}

export default function ScrollingLogos({ 
  logos, 
  backgroundColor = 'bg-white',
  speed = 'medium' 
}: ScrollingLogosProps) {
  // Speed configurations (in seconds for full animation)
  const speedConfig = {
    slow: 60,
    medium: 40,
    fast: 25
  }

  const animationDuration = speedConfig[speed]

  // Triple the logos array for truly seamless loop
  const duplicatedLogos = [...logos, ...logos, ...logos]

  return (
    <section className={`${backgroundColor} py-8 sm:py-10 lg:py-12 overflow-hidden border-b border-gray-100`}>
      <div className="relative">
        {/* Gradient overlays for fade effect */}
        <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
        
        {/* Scrolling container */}
        <div className="flex items-center">
          <div 
            className="flex items-center gap-8 sm:gap-12 lg:gap-16"
            style={{
              animation: 'scroll-infinite linear infinite',
              animationDuration: `${animationDuration}s`,
              willChange: 'transform'
            }}
          >
            {duplicatedLogos.map((logo, index) => (
              <div 
                key={`logo-${index}`}
                className="flex-shrink-0 flex items-center justify-center px-4 sm:px-6"
              >
                <Image
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  className={logo.className || 'h-8 sm:h-10 lg:h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity'}
                  priority={index < logos.length}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scroll-infinite {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
      `}</style>
    </section>
  )
}

